import type { RouteRecordForSolution } from '@navio/nsdk';
import { cn, cva, NoddiIcon } from 'noddi-ui-common';
import { getUserDisplayName, NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import type { ReactNode } from 'react';
import { WorkerAvatar } from '../ServiceWorker/WorkerAvatar';
import { RouteHeaderStats } from './RouteHeaderStats';
import type { RouteRowTone } from './routeRowTone';
import { getRoutePlannedTimes } from './routeStartTime';
import { RouteStatusBadges } from './RouteStatusBadges';
import { getServiceVehicleLabel } from './serviceVehicleLabel';

const rowVariants = cva(
  'flex w-full min-w-0 cursor-pointer gap-4 overflow-hidden text-left transition-colors focus-visible:ring-2 focus-visible:ring-grey focus-visible:outline-hidden focus-visible:ring-inset',
  {
    variants: {
      tone: {
        live: 'hover:bg-accent',
        // The washed surface sits on the wrapping card, so hovering lifts the row towards white.
        dim: 'hover:bg-white'
      }
    },
    defaultVariants: {
      tone: 'live'
    }
  }
);

const chevronVariants = cva('', {
  variants: {
    tone: {
      live: 'text-greyMuted',
      dim: 'text-darkPurple'
    }
  },
  defaultVariants: {
    tone: 'live'
  }
});

const nameVariants = cva('truncate text-sm text-foreground', {
  variants: {
    tone: {
      live: 'font-semibold',
      dim: 'font-medium'
    }
  },
  defaultVariants: {
    tone: 'live'
  }
});

type Props = {
  route: RouteRecordForSolution;
  isExpanded?: boolean;
  isRouteStatusPage?: boolean;
  onToggleExpand?: () => void;
  isWorkerMatch?: boolean;
  onWorkerClick?: () => void;
  actions?: ReactNode;
  bookingProgress?: ReactNode;
  solutionErrors?: ReactNode;
  numberOfStops: number;
  tone?: RouteRowTone;
};

export const RouteAccordionHeader = ({
  route,
  isExpanded,
  isRouteStatusPage,
  onToggleExpand,
  isWorkerMatch,
  onWorkerClick,
  actions,
  bookingProgress,
  solutionErrors,
  numberOfStops,
  tone
}: Props) => {
  const { capacityContribution } = route;
  const planned = getRoutePlannedTimes(route);

  const workerName = capacityContribution ? getUserDisplayName(capacityContribution.user) : null;
  const vehicleLabel = capacityContribution?.serviceVehicle
    ? getServiceVehicleLabel(capacityContribution.serviceVehicle)
    : null;

  return (
    <div
      role='button'
      tabIndex={0}
      className={cn(
        rowVariants({ tone }),
        // The status list is a single dense row per route; planning stacks solution errors and
        // keeps the original top-aligned geometry.
        isRouteStatusPage ? 'items-center p-4' : 'items-start px-4 py-3',
        isWorkerMatch && 'ring-2 ring-ring ring-offset-2'
      )}
      onClick={onToggleExpand}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggleExpand?.();
        }
      }}
    >
      {/* Content: name + stats + badges — wraps when narrow */}
      <div className='flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-2'>
        {/* Name group */}
        <div className='flex shrink-0 items-center gap-4'>
          <NoddiIcon
            name={isExpanded ? 'ChevronDown' : 'ChevronRight'}
            size='small'
            className={chevronVariants({ tone })}
          />
          {/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- worker name group nested in the row above, which is role='button' with its own Enter/Space handler; this click only diverts to onWorkerClick */}
          <div
            className={cn('flex items-center gap-2', isRouteStatusPage ? 'sm:w-56' : 'sm:w-44')}
            onClick={(e) => {
              if (!onWorkerClick) {
                return;
              }
              e.stopPropagation();
              onWorkerClick();
            }}
          >
            <WorkerAvatar
              name={workerName ?? undefined}
              size={isRouteStatusPage ? 'md' : 'sm'}
              variant={tone === 'dim' ? 'outline' : 'filled'}
            />
            <div className='flex min-w-0 flex-col'>
              <span className={cn(nameVariants({ tone }), onWorkerClick && 'hover:underline')}>
                {workerName ?? 'Unassigned'}
              </span>
              {/* The vehicle belongs to the worker's bookable time, so it reads under their name. */}
              {vehicleLabel && (
                <span className='flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground' title={vehicleLabel}>
                  <NoddiIcon name='Truck' size='xSmall' className='shrink-0' />
                  <span className='truncate'>{vehicleLabel}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        <RouteHeaderStats
          numberOfStops={numberOfStops}
          timeRange={`${planned.startsAt} - ${planned.endsAt}`}
          actualTimeRange={isRouteStatusPage ? getActualTimeRange(route) : undefined}
          totalDistanceInMeters={route.summary?.totalDistanceInMeters}
          bookingProgress={isRouteStatusPage ? bookingProgress : undefined}
          tone={tone}
        />

        {isRouteStatusPage ? (
          <RouteStatusBadges route={route} />
        ) : (
          solutionErrors != null && <div className='flex flex-wrap items-center gap-2 md:ml-auto'>{solutionErrors}</div>
        )}
      </div>

      {/* Actions — always top-right */}
      {actions}
    </div>
  );
};

/** When the route actually started and finished ("now" while it runs); null before it starts. */
const getActualTimeRange = (route: RouteRecordForSolution) => {
  if (!route.startedAt) {
    return null;
  }
  const startedAt = noddiFormatDate(route.startedAt, NoddiDateFormats.TIME);
  const completedAt = route.completedAt ? noddiFormatDate(route.completedAt, NoddiDateFormats.TIME) : 'now';
  return `${startedAt} - ${completedAt}`;
};
